/* ===================================================================
   useAdminTenantDashboard() -- the single admin-scoped Dashboard fetch
   that backs the operator read-only view (Option A; Liskov + Corbato,
   2026-06-22).

   GET /api/v1/admin/tenants/<id>/dashboard returns ONE bundle with every
   blob the client Dashboard reads (analytics summary, activity/gamification,
   notifications feed, request funnel, identity, business). The per-panel
   Dashboard hooks (useDashboardSummary / useActivity / useNotifications /
   useRequestsSummary / useMe / useBusiness), when rendered inside an
   AdminViewProvider, all read from THIS one query (same query key, so it is
   fetched once and cached) and select their slice -- instead of hitting their
   own per-tenant rr_-token endpoints.

   @require_admin (Supabase user JWT) + audited + READ-ONLY on the backend.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type AdminTenantDashboard } from '../lib/api';

/** Stable query key for one tenant's admin read-only dashboard bundle. */
export function adminDashboardKey(clientId: string) {
  return ['admin', 'tenant', clientId, 'dashboard'] as const;
}

export function useAdminTenantDashboard(
  clientId: string,
): UseQueryResult<AdminTenantDashboard, Error> {
  return useQuery({
    queryKey: adminDashboardKey(clientId),
    queryFn: ({ signal }) => api.getTenantDashboard(clientId, signal),
    enabled: Boolean(clientId),
    staleTime: 30_000,
  });
}
